import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import '../utils/visitAttribution';
import { getFacebookBrowserId, getFacebookClickId, getStoredTrackingIdentity, getTikTokBrowserId, getTikTokClickId, getTrackingExternalId } from '../utils/tracking';
import { getSalesSourceFromLocation, normalizeSalesSource } from '../utils/salesSource';
import { appendEmbedAuthorizationToPayload } from '../utils/embedAuthorization';
import { getConsentPreferences, toTrackingConsentPayload } from '../utils/consent';
import useConsentPreferences from '../hooks/useConsentPreferences';

const CartContext = createContext(null);

const getCartConsentSyncKey = (cart, consent) => {
  const cartReference = cart?.cartId || cart?._id || cart?.sessionId;
  if (!cartReference || !['decided', 'notice'].includes(consent?.status) || !consent?.decisionId || !consent?.decidedAt) return '';
  return [cartReference, consent.decisionId, consent.decidedAt, consent.marketing ? '1' : '0'].join('|');
};

const getConsentTrackingIdentifiers = (consent) => {
  if (consent?.marketing !== true) return {};
  const hostIdentifiers = consent.identifiers || {};
  return {
    fbc: hostIdentifiers.fbc || getFacebookClickId() || undefined,
    fbp: hostIdentifiers.fbp || getFacebookBrowserId() || undefined,
    ttclid: hostIdentifiers.ttclid || getTikTokClickId() || undefined,
    ttp: hostIdentifiers.ttp || getTikTokBrowserId() || undefined,
    matchingIdentity: getStoredTrackingIdentity()?.matchingIdentity,
    externalId: hostIdentifiers.externalId || getTrackingExternalId() || undefined
  };
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const consentPreferences = useConsentPreferences();
  const lastConsentSyncKey = useRef('');

  useEffect(() => {
    const syncKey = getCartConsentSyncKey(cart, consentPreferences);
    if (!syncKey || lastConsentSyncKey.current === syncKey) return undefined;

    lastConsentSyncKey.current = syncKey;
    let cancelled = false;
    let retryTimer = null;
    const syncConsent = async (attempt = 0) => {
      try {
        await api.post('/checkout/cart/consent', appendEmbedAuthorizationToPayload({
          cartId: cart.cartId || cart._id,
          sessionId: cart.sessionId,
          ...getConsentTrackingIdentifiers(consentPreferences),
          consent: toTrackingConsentPayload(consentPreferences)
        }));
      } catch (error) {
        if (!cancelled && attempt < 2) {
          retryTimer = window.setTimeout(() => syncConsent(attempt + 1), (attempt + 1) * 500);
        } else if (!cancelled) {
          lastConsentSyncKey.current = '';
        }
      }
    };
    syncConsent();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [cart, consentPreferences]);

  const addToCart = async (eventId, items, options = {}) => {
    setLoading(true);
    try {
      const sessionId = cart?.sessionId || localStorage.getItem('cartSessionId');
      const consentPreferences = getConsentPreferences();
      const trackingIdentifiers = getConsentTrackingIdentifiers(consentPreferences);
      const identity = getStoredTrackingIdentity();
      const salesSource = normalizeSalesSource(options.salesSource || getSalesSourceFromLocation());
      // Outlast the proxy's two 12-second attempts, but let a lost connection
      // release the pending UI. Retrying remains an explicit customer action.
      const res = await api.post('/checkout/cart', appendEmbedAuthorizationToPayload({
        eventId,
        items,
        sessionId,
        customerEmail: identity.email || undefined,
        customerFirstName: identity.firstName || undefined,
        customerLastName: identity.lastName || undefined,
        customerPhone: identity.phone || undefined,
        addToCartEventIds: options.addToCartEventIds || undefined,
        ticketAccessCode: options.ticketAccessCode || undefined,
        salesSource: salesSource || undefined,
        ...trackingIdentifiers,
        consent: toTrackingConsentPayload(consentPreferences),
        attribution: window.LVMVisitAttribution?.get(),
        pageUrl: window.LVMVisitAttribution?.pageUrl() || window.location.href,
        referrerUrl: document.referrer || ''
      }), { timeout: 30000 });
      window.LVMVisitAttribution?.confirmHandoff(res.data.data.attributionHandoffAccepted);
      lastConsentSyncKey.current = getCartConsentSyncKey(res.data.data, consentPreferences);
      setCart(res.data.data);
      localStorage.setItem('cartSessionId', res.data.data.sessionId);
      return res.data.data;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = () => {
    setCart(null);
    localStorage.removeItem('cartSessionId');
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, clearCart, setCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
